import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";

import { getGreetingFromDate } from "@/utils/date.util";
import { isWeb } from "@/utils/platform.util";

interface GetGreetingPropsParams {
  fixedHeight?: number;
  userName?: string;
}

interface GetGreetingProps {
  greetingText: string;
}

interface GetGreetingPaddingPropsParams {
  fixedHeight?: number;
  userName?: string;
}

interface GetGreetingPaddingProps {
  paddingBottom: number;
  paddingTop: number;
  shouldJustifyContent: boolean;
}

/**
 * Generates greeting text for the GreetingCard component.
 * Handles both web and native platforms with platform-specific formatting.
 */
const getGreetingProps = ({
  fixedHeight,
  userName,
}: GetGreetingPropsParams): GetGreetingProps => {
  const isInitialRender = isNil(fixedHeight);
  const greeting = getGreetingFromDate(new Date());

  // add a new line so that the initial render produces the height
  // that the card would have with an actual username on the next line
  const initialRenderText = `${greeting}\n`;

  const separator = isWeb() ? " " : "\n";
  const userSpecificText = isEmpty(userName)
    ? greeting
    : `${greeting},${separator}${userName}`;

  const greetingText = isInitialRender ? initialRenderText : userSpecificText;

  return {
    greetingText,
  };
};

/**
 * Calculates padding and justification based on platform and user state.
 */
const getGreetingPaddingProps = ({
  fixedHeight,
  userName,
}: GetGreetingPaddingPropsParams): GetGreetingPaddingProps => {
  const isInitialRender = isNil(fixedHeight);

  if (isWeb()) {
    return { paddingBottom: 36, paddingTop: 12, shouldJustifyContent: true };
  }

  if (!isInitialRender && isEmpty(userName)) {
    // if empty username: remove top/bottom padding so can 'center' text nicely
    return { paddingBottom: 0, paddingTop: 0, shouldJustifyContent: true };
  }

  return { paddingBottom: 36, paddingTop: 12, shouldJustifyContent: false };
};

export { getGreetingPaddingProps, getGreetingProps };
